import type { FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import type { Foundation } from "building-types/foundations/foundationSystemTypes";
import type {
  EdgeHandle,
  FloorEdgeHandle,
  FloorOpeningEdgeHandle,
  FoundationEdgeHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Roof } from "building-types/roofs/roofSystemTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { Railing } from "building-types/settings/railingTypes";
import { toTitleCase } from "common/utils/strings";
import { edgeIdsByPolyLine2 } from "geom/polyLine2";
import { getEdgeIdsForPerimeter, getFloorBoundaryFields } from "../docShapes";
import { railingGetCleanPath } from "../elements/railing/path";
import { isVirtualRoofBlock } from "../roofSystemTools";

export function polyLineEdgeNameAtIndex(edgeIndex: number, edgeCount: number): string | undefined {
  if (edgeIndex < 0 || edgeIndex >= edgeCount) {
    return undefined;
  }

  return "Edge " + (edgeIndex + 1);
}

export function roomEdgeLabel(room: StaticRoom, handle: EdgeHandle): string {
  return `${toTitleCase(handle.type)} ${room.edgeIds[handle.edgeId]}`;
}

export function foundationEdgeLabel(foundation: Foundation, handle: FoundationEdgeHandle): string {
  const edgeId = foundation.edgeIds?.[handle.edgeId];
  return `${toTitleCase(handle.type)} ${edgeId ?? handle.edgeId + 1}`;
}

export function floorEdgeLabel(floor: FloorAssemblyOverride, handle: FloorEdgeHandle | FloorOpeningEdgeHandle): string {
  const boundaryFields = getFloorBoundaryFields(floor);
  if ("openingIndex" in handle) {
    const openingEdgeIds = getEdgeIdsForPerimeter(
      boundaryFields.openingEdgeIds,
      boundaryFields.openingEdgeCounts,
      handle.openingIndex,
    );
    const edgeId = openingEdgeIds[handle.edgeId];
    return `Opening Edge ${edgeId ?? handle.edgeId + 1}`;
  }
  const perimeterEdgeIds = getEdgeIdsForPerimeter(
    boundaryFields.edgeIds,
    boundaryFields.perimeterEdgeCounts,
    handle.perimeterIndex,
  );
  const edgeId = perimeterEdgeIds[handle.edgeId];
  return `Edge ${edgeId ?? handle.edgeId + 1}`;
}

export function railingEdgeLabel(railing: Railing, edgeId: string): string | undefined {
  const path = railingGetCleanPath(railing);

  if (!path) {
    return undefined;
  }

  const edgeIds = edgeIdsByPolyLine2(path);
  const index = edgeIds.indexOf(edgeId);

  if (index === -1) {
    return undefined;
  }

  return `Edge ${edgeIds[index]}`;
}

export function roofEdgeLabel(roof: Roof, edgeId: string): string | undefined {
  if (!isVirtualRoofBlock(roof.blocks)) {
    return undefined;
  }

  const edgeIds = edgeIdsByPolyLine2(roof.blocks.perimeter);
  const index = edgeIds.indexOf(edgeId);

  if (index === -1) {
    return undefined;
  }

  return `Edge ${edgeIds[index]}`;
}
